/**
 * Moz Row Budget Tracking + Circuit-Breaker (Story 20.7, AC #8)
 *
 * Moz V3 charges per-row for list-style methods (a `data.site.top-pages.list`
 * call with `limit: 50` costs 50 rows; `data.site.metrics.fetch` costs 1 row;
 * `data.site.metrics.brand.authority.fetch` costs 1 row + 1 row per
 * `data.async-call.fetch` poll attempt). A sloppy widget config or runaway
 * cron can burn a customer's monthly row quota in hours.
 *
 * **Budget shape:** per-tenant 2,000-rows/day cap. Sized as a defensive default
 * for a 5-client agency on a 50K-rows/month Growth plan: 2K/day × 30 = 60K
 * capacity, slightly above the 50K plan to absorb intra-day variance. Catches
 * the "agency forgot to disconnect a stuck cron" footgun before it reaches the
 * server-side Moz quota (which would emit `error.code: rate_limit_exceeded`
 * for the rest of the billing cycle).
 *
 * **KV key shape:**
 *  - `moz:rows_used:tenant:{tenantId}:{YYYY-MM-DD}` — daily roll-up, 60-day TTL
 *  - `moz:rows_used:integration:{integrationId}:{YYYY-MM-DD}` — daily per-integration
 *  - `moz:budget_breach:{tenantId}:{YYYY-MM-DD}` — first-breach marker for notifications
 *
 * **The 60-day TTL on these KV keys is intentional and NOT subject to any Moz
 * data-persistence cap** (verified moz.com/legal/terms 2026-05-10 — no
 * equivalent to Semrush ToS §3.3 found). These are OBSERVABILITY records
 * about our own system (rows we consumed by our calls), not Moz API response
 * data — so the absence of a 30-day cap on response-data persistence does NOT
 * apply to these budget-tracking records.
 *
 * **No per-integration MONTHLY budget (DIVERGENT from Semrush).** Moz does
 * not have a separate Trends-API-style monthly add-on quota; all rows draw
 * from the same monthly plan allowance. We track per-tenant DAILY only to
 * catch runaway syncs early; the customer's monthly cap is enforced by Moz
 * server-side.
 */

import type { KVStore } from "../cache";

// ============================================================================
// Constants (AC #8)
// ============================================================================

/**
 * Per-tenant daily row budget. Calls are rejected with `rate_limited` once the
 * day's accumulated consumption crosses this threshold.
 *
 * Sized at 2,000/day = 60K/month capacity for a 50K-rows/month Moz Growth-tier
 * customer with the typical 5-client roster. Catches the "stuck cron" failure
 * mode before it depletes the monthly cap. Easy to tune via env override.
 */
export const MOZ_DAILY_TENANT_ROW_BUDGET = 2_000;

/** KV TTL for row-budget roll-up keys (60 days). */
export const MOZ_ROWS_KV_TTL_SEC = 60 * 24 * 3600;

// ============================================================================
// Hard ceiling on `limit` parameter (AC #4)
// ============================================================================

/**
 * Maximum `limit` allowed on a single Moz list-style call. Moz's API permits
 * up to 1000 rows per call but a sloppy widget config could burn 1000+ rows
 * in a single call. Round 1 finding: clamp at the helper level. 50 matches
 * the V2 `top_pages` default and the agency-reporting "top 50 pages" convention.
 *
 * Throws `TypeError` synchronously if exceeded — silent fallback would mask
 * widget-config bugs (carry-forward from Amazon Ads 19.16.1 + Semrush 20.4 R1).
 */
export const MOZ_LIST_LIMIT_MAX = 50;

// ============================================================================
// Key construction
// ============================================================================

export function buildMozTenantRowsKey(
  tenantId: string,
  yyyyMmDd: string,
): string {
  return `moz:rows_used:tenant:${tenantId}:${yyyyMmDd}`;
}

export function buildMozIntegrationRowsKey(
  integrationId: string,
  yyyyMmDd: string,
): string {
  return `moz:rows_used:integration:${integrationId}:${yyyyMmDd}`;
}

export function buildMozTenantBreachMarkerKey(
  tenantId: string,
  yyyyMmDd: string,
): string {
  return `moz:budget_breach:${tenantId}:${yyyyMmDd}`;
}

/** Today's date in `YYYY-MM-DD` UTC form. */
export function todayUtcYyyyMmDd(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

// ============================================================================
// KV roll-up helpers
// ============================================================================

/**
 * Increment per-integration AND per-tenant daily Moz row counters by `rows`.
 * Both share the 60-day TTL so the integration card UI can show "rows used
 * today" and "rows used this month" without separate jobs.
 *
 * NOTE: KV does not support atomic increment. Under concurrent calls, two
 * workers can read the same value, increment, and write back — losing one
 * increment. This is the same trade-off Semrush + Ahrefs accept; the Moz
 * server-side quota is the hard cap, our budget is a soft safety net.
 *
 * @returns the post-increment per-tenant total (for circuit-breaker checks).
 */
export async function incrementMozRowsUsed(
  kv: KVStore,
  tenantId: string,
  integrationId: string,
  rows: number,
  date: string = todayUtcYyyyMmDd(),
): Promise<number> {
  if (!Number.isFinite(rows) || rows <= 0) {
    return 0;
  }
  const tenantKey = buildMozTenantRowsKey(tenantId, date);
  const integrationKey = buildMozIntegrationRowsKey(integrationId, date);

  const [tenantRaw, integrationRaw] = await Promise.all([
    kv.get(tenantKey),
    kv.get(integrationKey),
  ]);

  const tenantTotal = (Number(tenantRaw ?? "0") || 0) + rows;
  const integrationTotal = (Number(integrationRaw ?? "0") || 0) + rows;

  await Promise.all([
    kv.put(tenantKey, String(tenantTotal), { expirationTtl: MOZ_ROWS_KV_TTL_SEC }),
    kv.put(integrationKey, String(integrationTotal), {
      expirationTtl: MOZ_ROWS_KV_TTL_SEC,
    }),
  ]);
  return tenantTotal;
}

export async function getMozTenantDailyRows(
  kv: KVStore,
  tenantId: string,
  date: string = todayUtcYyyyMmDd(),
): Promise<number> {
  const raw = await kv.get(buildMozTenantRowsKey(tenantId, date));
  return Number(raw ?? "0") || 0;
}

export async function getMozIntegrationDailyRows(
  kv: KVStore,
  integrationId: string,
  date: string = todayUtcYyyyMmDd(),
): Promise<number> {
  const raw = await kv.get(buildMozIntegrationRowsKey(integrationId, date));
  return Number(raw ?? "0") || 0;
}

// ============================================================================
// Circuit-breaker
// ============================================================================

export interface MozBudgetCheckResult {
  readonly exceeded: boolean;
  readonly current: number;
  readonly limit: number;
  /** True only on the FIRST check that observed the breach today. */
  readonly firstBreach: boolean;
}

/**
 * Per-tenant daily row-budget circuit-breaker. When the cap is crossed for
 * the first time today, a marker key is set so subsequent calls return
 * `firstBreach: false` (caller decides whether to fire the per-occurrence
 * notification). Mirrors `checkSemrushUnitBudget` semantics.
 */
export async function checkMozRowBudget(
  kv: KVStore,
  tenantId: string,
  limit: number = MOZ_DAILY_TENANT_ROW_BUDGET,
  date: string = todayUtcYyyyMmDd(),
): Promise<MozBudgetCheckResult> {
  const current = await getMozTenantDailyRows(kv, tenantId, date);
  const exceeded = current >= limit;
  if (!exceeded) {
    return { exceeded: false, current, limit, firstBreach: false };
  }
  const markerKey = buildMozTenantBreachMarkerKey(tenantId, date);
  const markerRaw = await kv.get(markerKey);
  const firstBreach = markerRaw === null;
  if (firstBreach) {
    await kv.put(markerKey, String(Date.now()), { expirationTtl: 24 * 3600 });
  }
  return { exceeded: true, current, limit, firstBreach };
}
